import React, { ReactNode } from 'react';
import {
  DefaultValues,
  FieldValues,
  FormProvider,
  Resolver,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { AnyObjectSchema } from 'yup';

export type FormProps<T extends FieldValues> = {
  defaultValues?: DefaultValues<T>;
  /**
   * Reactive external values: when this prop changes, the form resets to it.
   * Replaces the v5 wrapper's isChangeable/double-useEffect hack (native v7 feature).
   */
  values?: T;
  children: ReactNode;
  onSubmit: SubmitHandler<T>;
  validationSchema?: AnyObjectSchema;
  /** Passed to yup's validate({ context }) — same as v5 useYupValidationResolver's 2nd arg. */
  validationContext?: object;
  shouldUnregister?: boolean;
};

export const Form = <T extends FieldValues>({
  defaultValues,
  values,
  children,
  onSubmit,
  validationSchema,
  validationContext,
  shouldUnregister,
}: FormProps<T>) => {
  const methods = useForm<T>({
    defaultValues,
    values,
    context: validationContext,
    shouldUnregister,
    resolver: validationSchema
      ? (yupResolver(validationSchema) as unknown as Resolver<T>)
      : undefined,
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>{children}</form>
    </FormProvider>
  );
};
